import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import {
  X,
  Building2,
  UserPlus,
  Bell,
  BellOff,
  Tag,
  Check,
  ChevronDown,
  Plus,
  Trash2,
} from "lucide-react";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { commentsApi } from "@/features/tasks/api/comments";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  useTask,
  useUpdateTask,
  useDeleteTask,
  useAssignUser,
  useUnassignUser,
  useWatchTask,
} from "@/features/tasks/hooks/useTasks";
import {
  useTaskCategories,
  useCreateTaskCategory,
  useDeleteTaskCategory,
  useSetTaskCategory,
} from "@/features/tasks/hooks/useTaskCategories";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useProjects } from "@/features/tasks/hooks/useProjects";
import { SubTaskList } from "@/features/tasks/components/SubTaskList";
import { TaskCommentThread } from "@/features/tasks/components/TaskCommentThread";
import { SpreadsheetLinkList } from "@/features/tasks/components/SpreadsheetLinkList";
import {
  TaskMapTab,
  useTaskGISLinks,
} from "@/features/tasks/components/TaskMapTab";
import {
  useTaskSpreadsheetLinks,
  useCreateTaskSpreadsheetLink,
  useDeleteTaskSpreadsheetLink,
  useRenameTaskSpreadsheetLink,
  useUpdateTaskSpreadsheetLink,
} from "@/features/tasks/hooks/useSpreadsheetLinks";
import { useAuthStore } from "@/store/auth";
import { naturalSortKey } from "@/utils/naturalSort";

const PRESET_COLORS = [
  "#6366f1", "#3b82f6", "#06b6d4", "#10b981", "#84cc16",
  "#f59e0b", "#ef4444", "#ec4899", "#8b5cf6", "#64748b",
];

function CategoryPicker({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const ref = useRef<HTMLDivElement>(null);

  const { data: task } = useTask(taskId);
  const { data: categories = [] } = useTaskCategories();
  const { mutate: setCategory } = useSetTaskCategory(taskId);
  const { mutate: createCat, isPending: creating } = useCreateTaskCategory();
  const { mutate: deleteCat } = useDeleteTaskCategory();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const current = task?.my_category ?? null;

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    createCat({ name, color: newColor }, {
      onSuccess: (cat) => {
        setCategory(cat.id);
        setNewName("");
        setNewColor(PRESET_COLORS[0]);
        setAdding(false);
        setOpen(false);
      },
    });
  }

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Kategoria</p>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs rounded-full px-2.5 py-0.5 border transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
          style={current ? {
            backgroundColor: current.color + "22",
            borderColor: current.color + "66",
            color: current.color,
          } : undefined}
        >
          {current ? (
            <>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: current.color }} />
              {current.name}
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <Tag size={11} />
              Brak kategorii
            </span>
          )}
          <ChevronDown size={11} className="opacity-50" />
        </button>
        {current && (
          <button
            onClick={() => setCategory(null)}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors"
            title="Usuń kategorię"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-8 z-50 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Wybierz kategorię</span>
          </div>
          <div className="py-1">
            <button
              onClick={() => { setCategory(null); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <span className="w-3 h-3 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-500" />
              Brak kategorii
              {!current && <Check size={13} className="ml-auto text-[var(--color-primary)]" />}
            </button>
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 group/ci cursor-pointer"
                onClick={() => { setCategory(cat.id); setOpen(false); }}
              >
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cat.color }} />
                <span className="text-sm flex-1 truncate text-gray-700 dark:text-gray-300">{cat.name}</span>
                {current?.id === cat.id && <Check size={13} className="text-[var(--color-primary)] flex-shrink-0" />}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Usuń kategorię „${cat.name}"?`)) {
                      if (current?.id === cat.id) setCategory(null);
                      deleteCat(cat.id);
                    }
                  }}
                  className="hidden group-hover/ci:block p-0.5 text-gray-400 hover:text-red-500 flex-shrink-0"
                  title="Usuń kategorię"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700">
            {adding ? (
              <form onSubmit={submitCreate} className="px-3 py-2 space-y-1.5">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="Nazwa nowej kategorii"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewColor(c)}
                      className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? "ring-2 ring-offset-1 ring-gray-400 scale-110" : ""}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <button type="submit" disabled={creating || !newName.trim()} className="flex-1 text-xs bg-[var(--color-primary)] text-white rounded px-2 py-1 hover:bg-[var(--color-primary-hover)] disabled:opacity-50">
                    Dodaj
                  </button>
                  <button type="button" onClick={() => { setAdding(false); setNewName(""); }} className="flex-1 text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                    Anuluj
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <Plus size={12} />
                Nowa kategoria
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function DescriptionContent({ content }: { content: string }) {
  if (!content) return null;
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-600 dark:text-gray-400"
        dangerouslySetInnerHTML={{ __html: sanitize(content) }}
      />
    );
  }
  return <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">{content}</p>;
}

interface Props {
  taskId: string;
  onClose: () => void;
  initialTab?: "subtasks" | "comments" | "sheets" | "map";
  targetCommentId?: string | null;
}

export function TaskDetailPanel({ taskId, onClose, initialTab, targetCommentId }: Props) {
  const { data: task, isLoading } = useTask(taskId);
  const { data: allUsers = [] } = useInternalUsers({ includeFridayContractors: true, sameBranch: true });
  const { data: projects = [] } = useProjects();
  const { mutate: updateTask } = useUpdateTask(taskId);
  const { mutate: deleteTask } = useDeleteTask();
  const { mutate: assignUser } = useAssignUser(taskId);
  const { mutate: unassignUser } = useUnassignUser(taskId);
  const { mutate: watchTask, isPending: watchPending } = useWatchTask(taskId);
  const currentUser = useAuthStore((s) => s.user);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [assignPickerSearch, setAssignPickerSearch] = useState("");
  const [activeTab, setActiveTab] = useState<
    "subtasks" | "comments" | "sheets" | "map"
  >(initialTab ?? "subtasks");
  const { data: spreadsheetLinks = [], isLoading: linksLoading } =
    useTaskSpreadsheetLinks(taskId);
  const { data: gisLinks = [] } = useTaskGISLinks(taskId);
  const { mutateAsync: createLink, isPending: creatingLink } =
    useCreateTaskSpreadsheetLink(taskId);
  const { mutate: deleteLink } = useDeleteTaskSpreadsheetLink(taskId);
  const { mutateAsync: renameLink } = useRenameTaskSpreadsheetLink(taskId);
  const { mutateAsync: updateLink } = useUpdateTaskSpreadsheetLink(taskId);

  if (isLoading || !task) {
    return (
      <>
        <div className="fixed inset-0 z-[75] bg-black/20" onClick={onClose} />
        <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-gray-800 shadow-2xl z-[80] flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  const assignedIds = new Set(task.assignees.map((a) => a.id));
  const canModify =
    currentUser?.role === "director" ||
    currentUser?.role === "coordinator" ||
    task.created_by === currentUser?.id ||
    task.assignees.some((a) => a.id === currentUser?.id);
  const canUnassign =
    currentUser?.is_ceo ||
    currentUser?.role === "director" ||
    (currentUser?.role === "coordinator" &&
      task.assignees.some((a) => a.id === currentUser?.id));

  function saveTitle() {
    const t = titleDraft.trim();
    if (t && t !== task!.title) updateTask({ title: t });
    setEditingTitle(false);
  }

  function saveDesc() {
    updateTask({ description: descDraft });
    setEditingDesc(false);
  }

  function handleDelete() {
    if (!window.confirm("Usunąć to zadanie?")) return;
    deleteTask(taskId, { onSuccess: onClose });
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[75] bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-gray-800 shadow-2xl z-[80] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-2 sm:pr-4">
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => e.key === "Enter" && saveTitle()}
                className="w-full text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 border-b-2 border-[var(--color-primary)] outline-none bg-transparent"
              />
            ) : (
              <h2
                className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 break-words cursor-pointer hover:text-[var(--color-primary)]"
                onClick={() => {
                  setTitleDraft(task.title);
                  setEditingTitle(true);
                }}
                title="Kliknij aby edytować"
              >
                {task.title}
              </h2>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => watchTask(!task.is_watching)}
              disabled={watchPending}
              title={
                task.is_watching
                  ? "Wypisz się z powiadomień"
                  : "Subskrybuj powiadomienia"
              }
              className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                task.is_watching
                  ? "text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-600 hover:bg-gray-100"
              }`}
            >
              {task.is_watching ? <Bell size={17} /> : <BellOff size={17} />}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 p-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 flex flex-col gap-5">
          {/* Project */}
          {canModify ? (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Building2 size={13} className="flex-shrink-0" />
              <SearchableSelect
                variant="ghost"
                value={task.project ?? ""}
                onChange={(e) =>
                  updateTask({ project: e.target.value || null })
                }
                placeholder="— brak przedsięwzięcia —"
                className="max-w-[18rem]"
                options={projects
                  .slice()
                  .sort((a, b) =>
                    naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name), "pl"),
                  )
                  .map((p) => ({ value: p.id, label: `${p.short_name} – ${p.name}` }))}
              />
            </div>
          ) : task.project_name ? (
            <div className="flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Building2 size={13} className="flex-shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0 break-words">
                {task.project_name}
              </span>
            </div>
          ) : null}

          {/* Category */}
          <CategoryPicker taskId={taskId} />

          {/* Description */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Opis
            </p>
            {editingDesc ? (
              <div>
                <RichTextEditor
                  value={descDraft}
                  onChange={setDescDraft}
                  placeholder="Opisz zadanie…"
                  onImageUpload={(file) => commentsApi.uploadDescriptionImage(taskId, file)}
                />
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={saveDesc}
                    className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] font-medium"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={() => setEditingDesc(false)}
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => {
                  setDescDraft(task.description ?? "");
                  setEditingDesc(true);
                }}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded px-1 -ml-1 py-1 min-h-[2rem]"
              >
                {task.description ? (
                  <DescriptionContent content={task.description} />
                ) : (
                  <span className="text-sm text-gray-400 dark:text-gray-500 italic">Kliknij aby dodać opis...</span>
                )}
              </div>
            )}
          </div>

          {/* Assignees */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Przypisani
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {task.assignees.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-full pl-0.5 pr-2 py-0.5"
                >
                  <UserAvatar
                    userId={a.id}
                    fullName={a.full_name}
                    profilePictureUrl={a.profile_picture_url}
                    size="sm"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
                    {a.full_name}
                  </span>
                  {canUnassign && (
                    <button
                      onClick={() => unassignUser(a.id)}
                      className="text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
              {canModify && (
                <div className="relative">
                  <button
                    onClick={() => { setShowAssignPicker((v) => !v); setAssignPickerSearch(""); }}
                    className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)]"
                  >
                    <UserPlus size={13} />
                    Dodaj
                  </button>
                  {showAssignPicker && (
                    <div className="absolute top-6 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 w-56 flex flex-col py-1">
                      <div className="px-2 pb-1">
                        <input
                          autoFocus
                          type="text"
                          placeholder="Szukaj..."
                          value={assignPickerSearch}
                          onChange={(e) => setAssignPickerSearch(e.target.value)}
                          className="w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                        />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {allUsers
                          .filter((u) => !assignedIds.has(u.id))
                          .filter((u) => u.full_name.toLowerCase().includes(assignPickerSearch.toLowerCase()))
                          .map((u) => (
                            <button
                              key={u.id}
                              onClick={() => {
                                assignUser(u.id);
                                setShowAssignPicker(false);
                                setAssignPickerSearch("");
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--color-primary-dim)] flex items-center gap-2"
                            >
                              <UserAvatar
                                fullName={u.full_name}
                                profilePictureUrl={u.profile_picture_url}
                                size="xs"
                              />
                              {u.full_name}
                            </button>
                          ))}
                        {allUsers.filter((u) => !assignedIds.has(u.id)).filter((u) => u.full_name.toLowerCase().includes(assignPickerSearch.toLowerCase())).length === 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-1.5 italic">
                            {assignPickerSearch ? "Brak wyników" : "Wszyscy przypisani"}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div>
            <div className="flex gap-2 sm:gap-4 border-b border-gray-100 mb-3 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              {(["subtasks", "comments", "sheets", "map"] as const).map(
                (tab) => {
                  const label =
                    tab === "subtasks"
                      ? "Podzadania"
                      : tab === "comments"
                        ? "Komentarze"
                        : tab === "sheets"
                          ? "Arkusze"
                          : "Mapy";
                  const count =
                    tab === "subtasks"
                      ? `${task.done_subtask_count}/${task.subtask_count}`
                      : tab === "sheets" && spreadsheetLinks.length > 0
                        ? `${spreadsheetLinks.length}`
                        : tab === "map" && gisLinks.length > 0
                          ? `${gisLinks.length}`
                          : null;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`text-xs sm:text-sm pb-2 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab
                          ? "border-b-2 border-[var(--color-primary)] text-[var(--color-primary)]"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
                      }`}
                    >
                      {label}
                      {count && (
                        <span className="ml-1 opacity-60 text-[11px] sm:text-xs">
                          ({count})
                        </span>
                      )}
                    </button>
                  );
                },
              )}
            </div>

            {activeTab === "subtasks" && <SubTaskList taskId={taskId} />}
            {activeTab === "comments" && <TaskCommentThread taskId={taskId} targetCommentId={targetCommentId} />}
            {activeTab === "map" && <TaskMapTab taskId={taskId} />}
            {activeTab === "sheets" && (
              <SpreadsheetLinkList
                links={spreadsheetLinks}
                isLoading={linksLoading}
                onCreate={createLink}
                onDelete={deleteLink}
                onRename={(id, name) => renameLink({ linkId: id, name })}
                onUpdate={(id, data) => updateLink({ linkId: id, data })}
                isCreating={creatingLink}
                taskId={taskId}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-gray-100 flex justify-end flex-shrink-0">
          <button
            onClick={handleDelete}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Usuń zadanie (wraz z podzadaniami)
          </button>
        </div>
      </div>
    </>
  );
}
